import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'
import * as XLSX from 'xlsx'

/* ─── design tokens ─── */
const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', blueD:'#2563eb', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    red:'#dc2626', redBg:'#fef2f2', redBdr:'#fecaca',
    warn:'#f59e0b',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

/* ─── MultiSelect reutilizable ─── */
function MultiSelect({ label, options, value, onChange, getLabel, getId, placeholder = 'Todos' }) {
    const [open, setOpen]   = useState(false)
    const [query, setQuery] = useState('')
    const inputRef          = useRef(null)
    const containerRef      = useRef(null)

    useEffect(() => {
        const handler = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const filtered = options.filter(o =>
        getLabel(o).toLowerCase().includes(query.toLowerCase())
    )
    const isSelected = o => value.some(v => getId(v) === getId(o))
    const toggle = o => {
        onChange(isSelected(o) ? value.filter(v => getId(v) !== getId(o)) : [...value, o])
    }

    return (
        <div ref={containerRef} style={{ position:'relative', flex:1, minWidth:160 }}>
            {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.txt2, marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</label>}
            <div onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50) }}
                style={{ minHeight:38, padding:'4px 10px', borderRadius:T.rsm, border:`1px solid ${open ? T.blue : T.border}`,
                    background:T.card, cursor:'pointer', display:'flex', flexWrap:'wrap', gap:4, alignItems:'center',
                    boxShadow: open ? `0 0 0 3px ${T.blue}20` : T.shadow, transition:'all .15s' }}>
                {value.length === 0 && !open && (
                    <span style={{ fontSize:13, color:T.muted }}>{placeholder}</span>
                )}
                {value.map(v => (
                    <span key={getId(v)} style={{ display:'inline-flex', alignItems:'center', gap:4, background:`${T.blue}12`,
                        border:`1px solid ${T.blue}30`, borderRadius:6, padding:'1px 7px', fontSize:12, color:T.blueD, fontWeight:500 }}>
                        {getLabel(v)}
                        <span onMouseDown={e => { e.preventDefault(); e.stopPropagation(); toggle(v) }}
                            style={{ cursor:'pointer', color:T.blue, fontWeight:700, lineHeight:1 }}>×</span>
                    </span>
                ))}
                {open && (
                    <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                        onMouseDown={e => e.stopPropagation()}
                        placeholder="Buscar..." autoComplete="off"
                        style={{ border:'none', outline:'none', fontSize:13, color:T.txt, background:'transparent',
                            minWidth:80, flex:1, fontFamily:T.font }} />
                )}
            </div>
            {open && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, marginTop:4,
                    background:T.card, border:`1px solid ${T.border}`, borderRadius:T.rsm,
                    boxShadow:'0 8px 24px rgba(15,23,42,.12)', maxHeight:220, overflowY:'auto' }}>
                    {filtered.length === 0
                        ? <div style={{ padding:'10px 14px', fontSize:13, color:T.muted }}>Sin resultados</div>
                        : filtered.map(o => (
                            <div key={getId(o)} onMouseDown={e => { e.preventDefault(); toggle(o) }}
                                style={{ padding:'9px 14px', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8,
                                    background: isSelected(o) ? `${T.blue}08` : 'transparent',
                                    color: isSelected(o) ? T.blue : T.txt, fontWeight: isSelected(o) ? 600 : 400 }}>
                                <span style={{ width:15, height:15, borderRadius:4, border:`1.5px solid ${isSelected(o) ? T.blue : T.border}`,
                                    background: isSelected(o) ? T.blue : 'transparent', display:'inline-flex',
                                    alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                    {isSelected(o) && <span style={{ color:'white', fontSize:10, fontWeight:800 }}>✓</span>}
                                </span>
                                {getLabel(o)}
                            </div>
                        ))
                    }
                </div>
            )}
        </div>
    )
}

/* ─── Badge de opinión ─── */
function OpinionBadge({ opinion }) {
    const pos = opinion === 'P'
    return (
        <span style={{
            display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700,
            background: pos ? T.greenBg : T.redBg,
            border: `1px solid ${pos ? T.greenBdr : T.redBdr}`,
            color: pos ? T.green : T.red,
        }}>
            {pos ? 'POSITIVA' : 'NEGATIVA'}
        </span>
    )
}

const OPINIONES = [{ label:'POSITIVA', value:'P' }, { label:'NEGATIVA', value:'N' }]

export default function OpinionesCumplimientoReporte() {
    const history = useHistory()
    const { id }  = useParams()

    const [opiniones,     setOpiniones]     = useState([])
    const [allSociedades, setAllSociedades] = useState([])
    const [allClientes,   setAllClientes]   = useState([])
    const [allResp,       setAllResp]       = useState([])
    const [allAux,        setAllAux]        = useState([])
    const [loading,       setLoading]       = useState(true)

    // filtros
    const [fClientes,   setFClientes]   = useState([])
    const [fSociedades, setFSociedades] = useState([])
    const [fResp,       setFResp]       = useState([])
    const [fAux,        setFAux]        = useState([])
    const [fOpinion,    setFOpinion]    = useState([])

    // paginación
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 50

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('READ_CONTABILIDAD')) {
            history.push('/login')
            return
        }
        if (Number(id) === -1) { setLoading(false); return }

        Promise.all([
            ElementsDataService.retrieveAllElements('cfdis/opiniones'),
            ElementsDataService.retrieveAllElements('sociedades'),
            ElementsDataService.retrieveAllElements('clientes'),
            ElementsDataService.retrieveAllElements('responsablesContabilidad'),
            ElementsDataService.retrieveAllElements('auxiliaresContables'),
        ]).then(([op, soc, cli, resp, aux]) => {
            setOpiniones(op.data || [])
            setAllSociedades(soc.data || [])
            setAllClientes(cli.data || [])
            setAllResp(resp.data || [])
            setAllAux(aux.data || [])
        }).finally(() => setLoading(false))
    }, []) // eslint-disable-line

    /* ── filtrado ── */
    const clientesIds   = fClientes.map(c => c.id)
    const sociedadesIds = fSociedades.map(s => s.id)
    const respCodigos   = fResp.map(r => r.codigo)
    const auxCodigos    = fAux.map(a => a.codigo)
    const opVals        = fOpinion.map(o => o.value)

    const filtered = opiniones.filter(op => {
        if (clientesIds.length   && !clientesIds.includes(op.clienteId))                     return false
        if (sociedadesIds.length && !sociedadesIds.includes(op.sociedadId))                  return false
        if (respCodigos.length   && !respCodigos.includes(op.responsableContabilidad))       return false
        if (auxCodigos.length    && !auxCodigos.includes(op.auxiliarContable))               return false
        if (opVals.length        && !opVals.includes(op.opinion))                            return false
        return true
    })

    const totalPages  = Math.ceil(filtered.length / PAGE_SIZE)
    const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    const resetPage = () => setPage(1)

    /* ── Excel export ── */
    const downloadExcel = () => {
        const data = filtered.map(op => ({
            'Cliente':               op.nombreCliente       ?? '',
            'Razón Social':          op.razonSocial         ?? '',
            'RFC':                   op.rfc                 ?? '',
            'Responsable':           op.responsableContabilidad ?? '',
            'Auxiliar Contable':     op.auxiliarContable    ?? '',
            'Fecha':                 op.fecha               ?? '',
            'Opinión':               op.opinion === 'P' ? 'POSITIVA' : 'NEGATIVA',
            'Observaciones':         op.respuestaDeOpinion  ?? '',
        }))
        const ws = XLSX.utils.json_to_sheet(data)
        ws['!cols'] = [
            { wch:18 }, { wch:38 }, { wch:16 }, { wch:22 },
            { wch:22 }, { wch:14 }, { wch:12 }, { wch:50 },
        ]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Opiniones Cumplimiento')
        XLSX.writeFile(wb, `Opiniones_Cumplimiento_${new Date().toISOString().slice(0,10)}.xlsx`)
    }

    /* ── helpers estilos tabla ── */
    const th = { padding:'10px 14px', fontSize:11, fontWeight:700, color:T.txt2,
        textTransform:'uppercase', letterSpacing:'.05em', whiteSpace:'nowrap',
        borderBottom:`2px solid ${T.border}`, background:T.card, textAlign:'left' }
    const td = { padding:'10px 14px', fontSize:13, color:T.txt, borderBottom:`1px solid ${T.border}`, verticalAlign:'middle' }

    if (loading) return (
        <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:T.font }}>
            <div style={{ textAlign:'center' }}>
                <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTopColor:T.blue,
                    borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 12px' }} />
                <p style={{ color:T.txt2, fontSize:14, margin:0 }}>Cargando datos...</p>
            </div>
            <style>{`@keyframes spin { to { transform:rotate(360deg) } }`}</style>
        </div>
    )

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`}</style>

            <div style={{ maxWidth:1400, margin:'0 auto' }}>

                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <button onClick={() => history.goBack()}
                            style={{ width:34, height:34, borderRadius:9, border:`1px solid ${T.border}`,
                                background:T.card, cursor:'pointer', fontSize:16, color:T.txt2,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                boxShadow:T.shadow, flexShrink:0, fontFamily:T.font, transition:'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                            onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                            ‹
                        </button>
                        <div>
                            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>
                                Reporte Opiniones de Cumplimiento
                            </h1>
                            <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>
                                {opiniones.length} registros totales
                            </p>
                        </div>
                    </div>
                    <button onClick={downloadExcel} disabled={filtered.length === 0}
                        style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 18px',
                            borderRadius:T.rsm, border:'none', background: filtered.length === 0 ? T.muted : T.blue,
                            color:'white', fontSize:13, fontWeight:700, cursor: filtered.length === 0 ? 'not-allowed':'pointer',
                            fontFamily:T.font, boxShadow: filtered.length > 0 ? `0 4px 12px ${T.blue}40`:'none', transition:'all .15s' }}>
                        📥 Descargar Excel
                    </button>
                </div>

                {/* Filtros */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r,
                    boxShadow:T.shadow, padding:'16px 20px', marginBottom:16 }}>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:12 }}>
                        <MultiSelect label="Clientes"   options={allClientes}   value={fClientes}
                            onChange={v => { setFClientes(v); resetPage() }}
                            getLabel={o => o.nombreCliente} getId={o => o.id} />
                        <MultiSelect label="Sociedades" options={allSociedades} value={fSociedades}
                            onChange={v => { setFSociedades(v); resetPage() }}
                            getLabel={o => `${o.razonSocial} ${o.rfc}`} getId={o => o.rfc} />
                        <MultiSelect label="Opinión"    options={OPINIONES}     value={fOpinion}
                            onChange={v => { setFOpinion(v); resetPage() }}
                            getLabel={o => o.label} getId={o => o.value} />
                    </div>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                        <MultiSelect label="Responsable Contabilidad" options={allResp} value={fResp}
                            onChange={v => { setFResp(v); resetPage() }}
                            getLabel={o => o.codigo} getId={o => o.codigo} />
                        <MultiSelect label="Auxiliar Contable" options={allAux} value={fAux}
                            onChange={v => { setFAux(v); resetPage() }}
                            getLabel={o => o.codigo} getId={o => o.codigo} />
                        {(fClientes.length > 0 || fSociedades.length > 0 || fResp.length > 0 || fAux.length > 0 || fOpinion.length > 0) && (
                            <div style={{ display:'flex', alignItems:'flex-end' }}>
                                <button onClick={() => { setFClientes([]); setFSociedades([]); setFResp([]); setFAux([]); setFOpinion([]); resetPage() }}
                                    style={{ padding:'7px 14px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                        background:T.bg, color:T.txt2, fontSize:12, cursor:'pointer', fontFamily:T.font,
                                        whiteSpace:'nowrap', height:38 }}>
                                    ✕ Limpiar filtros
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabla */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>
                    {/* Cabecera tabla */}
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`,
                        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span>📋</span>
                            <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>Resultados</h2>
                            <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`,
                                border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>
                                {filtered.length}
                            </span>
                        </div>
                        {filtered.length > PAGE_SIZE && (
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                                    style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`,
                                        background: page===1 ? T.bg : T.card, color: page===1 ? T.muted : T.txt,
                                        cursor: page===1 ? 'default':'pointer', fontSize:13, fontFamily:T.font }}>‹</button>
                                <span style={{ fontSize:12, color:T.txt2, whiteSpace:'nowrap' }}>
                                    {page} / {totalPages}
                                    <span style={{ color:T.muted }}> ({(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, filtered.length)} de {filtered.length})</span>
                                </span>
                                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
                                    style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`,
                                        background: page===totalPages ? T.bg : T.card, color: page===totalPages ? T.muted : T.txt,
                                        cursor: page===totalPages ? 'default':'pointer', fontSize:13, fontFamily:T.font }}>›</button>
                            </div>
                        )}
                    </div>

                    {filtered.length === 0 ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ fontSize:36, marginBottom:10 }}>🔍</div>
                            <p style={{ margin:0, fontSize:14, fontWeight:600, color:T.txt }}>Sin resultados</p>
                            <p style={{ margin:'4px 0 0', fontSize:13, color:T.muted }}>Ajusta los filtros para ver opiniones</p>
                        </div>
                    ) : (
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                                <thead>
                                    <tr>
                                        {['Cliente','Razón Social','RFC','Responsable','Auxiliar','Fecha','Opinión','Observaciones','Archivo'].map(h => (
                                            <th key={h} style={th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((op, i) => (
                                        <tr key={op.id ?? i}
                                            style={{ background: i % 2 === 0 ? T.card : T.bg, transition:'background .15s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = `${T.blue}06`}
                                            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.card : T.bg}>
                                            <td style={td}>{op.nombreCliente}</td>
                                            <td style={{ ...td, fontWeight:500 }}>{op.razonSocial}</td>
                                            <td style={{ ...td, fontFamily:'monospace', fontSize:12, color:T.txt2 }}>{op.rfc}</td>
                                            <td style={{ ...td, fontSize:12 }}>{op.responsableContabilidad || '—'}</td>
                                            <td style={{ ...td, fontSize:12 }}>{op.auxiliarContable || '—'}</td>
                                            <td style={{ ...td, fontSize:12, whiteSpace:'nowrap', color:T.txt2 }}>{op.fecha}</td>
                                            <td style={td}><OpinionBadge opinion={op.opinion} /></td>
                                            <td style={{ ...td, fontSize:12, maxWidth:220 }}>
                                                <div dangerouslySetInnerHTML={{ __html: op.respuestaDeOpinion }}
                                                    style={{ maxHeight:60, overflow:'hidden', textOverflow:'ellipsis' }} />
                                            </td>
                                            <td style={{ ...td, textAlign:'center' }}>
                                                {op.documentFileUrl ? (
                                                    <button onClick={() => ElementsDataService.downloadS3File(
                                                        `?fileName=${op.documentFileUrl}`,
                                                        `${op.razonSocial}_${op.rfc}.pdf`
                                                    )}
                                                        style={{ padding:'5px 12px', borderRadius:7, border:`1px solid ${T.greenBdr}`,
                                                            background:T.greenBg, color:T.green, fontSize:12, fontWeight:600,
                                                            cursor:'pointer', fontFamily:T.font, whiteSpace:'nowrap' }}>
                                                        ⬇ PDF
                                                    </button>
                                                ) : (
                                                    <span style={{ color:T.muted, fontSize:18 }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}
